import type { TrustSummaryFacts } from '@/ui/kit/TrustSummary';
import { api } from './client';
import type { Commitment, CommitmentDetail, DistanceTier } from './types';

export interface Candidate {
  id: string;
  name: string;
  accountType: string;
  trustScore?: number;
  /** specs/003 #8: concrete facts shown instead of the percentage. */
  trustSummary?: TrustSummaryFacts | null;
  skills: string[];
  approximateDistanceKm: number | null;
  /** specs/003 #1: shown instead of the figure. */
  distanceTier?: DistanceTier | null;
  photoUrl?: string;
  /** Short intro line (profile description, else a skills summary). */
  blurb?: string | null;
  /** Joined recently: the "New neighbor" badge. */
  isNew?: boolean;
  vouchCount?: number;
  vouchedByViewer?: boolean;
}

export interface Kudos {
  id: string;
  commitmentId: string;
  thankYouNoteText?: string;
  kudosTraits?: string[];
}

/** FR-013–FR-017: matching, commitments, vouches and kudos (mobile/src/services/matchingApi.ts). */
/** specs/003 #10: what can go wrong in a help arrangement. */
export type InteractionProblem =
  | 'no_show'
  | 'late_cancellation'
  | 'damage_or_loss'
  | 'unsafe_behaviour'
  | 'payment_requested'
  | 'other';

export const matchingApi = {
  /** Reports a problem with a help arrangement; the reported member cannot contact you while it is open. */
  reportProblem: (commitmentId: string, category: InteractionProblem, details?: string) =>
    api.post<unknown>(
      `/commitments/${encodeURIComponent(commitmentId)}/problems`,
      details ? { category, details } : { category },
    ),

  getCandidates: (postId: string) => api.get<Candidate[]>(`/posts/${postId}/candidates`),

  getCommitment: (commitmentId: string) =>
    api.get<CommitmentDetail>(`/commitments/${commitmentId}`),

  createCommitment: (postId: string, helperId: string) =>
    api.post<Commitment>(`/posts/${postId}/commitments`, { helperId }),

  advanceStage: (commitmentId: string) =>
    api.patch<Commitment>(`/commitments/${commitmentId}/stage`),

  setSafetyCheckIn: (commitmentId: string, enabled: boolean) =>
    api.patch<Commitment>(`/commitments/${commitmentId}/safety-check-in`, { enabled }),

  setMeetingTime: (commitmentId: string, meetingAt: string | null) =>
    api.patch<Commitment>(`/commitments/${commitmentId}/meeting-time`, { meetingAt }),

  replySafety: (commitmentId: string, status: 'ok' | 'help') =>
    api.post<Commitment>(`/commitments/${commitmentId}/safety-reply`, { status }),

  proposeMeetingSpot: (commitmentId: string, meetingSpot: string) =>
    api.patch<Commitment>(`/commitments/${commitmentId}/meeting-spot`, { meetingSpot }),

  arrived: (commitmentId: string) => api.post<Commitment>(`/commitments/${commitmentId}/arrived`),

  release: (commitmentId: string) => api.post<Commitment>(`/commitments/${commitmentId}/release`),

  sendThankYou: (commitmentId: string, note?: string) =>
    api.post<Kudos>(`/commitments/${commitmentId}/thank-you`, { note }),

  giveKudos: (commitmentId: string, traits: string[]) =>
    api.post<Kudos>(`/commitments/${commitmentId}/kudos`, { traits }),

  vouch: (memberId: string) =>
    api.post<{ memberId: string; vouched: boolean; vouchCount: number }>(
      `/members/${memberId}/vouch`,
    ),
};
